import { Link } from "@tanstack/react-router";
import { Fuel, Gauge, Users } from "lucide-react";

import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import type { Car } from "@/data/cars";
import { CarPhoto } from "@/components/site/CarPhoto";

/** Presentation-only promo pricing: a headline discount on hand-picked cars. */
const DISCOUNT = 0.15;

export function PromoCarsCarousel({ cars }: { cars: Car[] }) {
  return (
    <Carousel opts={{ align: "start", loop: true }} className="w-full">
      <CarouselContent className="-ml-4">
        {cars.map((car) => {
          const promoPrice = Math.round(car.pricePerDay * (1 - DISCOUNT));
          return (
            <CarouselItem key={car.id} className="pl-4 sm:basis-1/2 lg:basis-1/3">
              <article className="group flex h-full flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-card transition-all hover:shadow-lift">
                <Link
                  to="/rentals/$slug"
                  params={{ slug: car.slug }}
                  className="photo-stage relative block overflow-hidden"
                >
                  <CarPhoto
                    src={car.images[0]}
                    alt={`${car.brand} ${car.name.split(":")[0]} on promotion in Bucharest`}
                    className="aspect-[3/2] w-full p-4 transition-transform duration-500 group-hover:scale-[1.04]"
                  />
                  <Badge className="absolute left-3 top-3 bg-gold text-gold-foreground">
                    -{Math.round(DISCOUNT * 100)}%
                  </Badge>
                </Link>
                <div className="flex flex-1 flex-col gap-3 p-5">
                  <h3 className="line-clamp-2 font-display text-base font-bold leading-snug">
                    <Link to="/rentals/$slug" params={{ slug: car.slug }} className="hover:text-gold">
                      {car.name}
                    </Link>
                  </h3>
                  <div className="flex flex-wrap gap-x-4 gap-y-1.5 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1.5">
                      <Gauge className="size-3.5 text-gold" /> {car.transmission}
                    </span>
                    <span className="flex items-center gap-1.5">
                      <Fuel className="size-3.5 text-gold" /> {car.fuelType}
                    </span>
                    <span className="flex items-center gap-1.5">
                      <Users className="size-3.5 text-gold" /> {car.seats} seats
                    </span>
                  </div>
                  <div className="mt-auto flex items-center justify-between gap-3 border-t border-border pt-4">
                    <p className="font-display text-lg font-bold">
                      €{promoPrice}
                      <span className="ml-2 text-xs font-medium text-muted-foreground line-through">
                        €{car.pricePerDay}
                      </span>
                      <span className="text-xs font-medium text-muted-foreground"> / Day</span>
                    </p>
                    <Button asChild variant="gold" size="sm">
                      <Link
                        to="/rentals/$slug"
                        params={{ slug: car.slug }}
                        aria-label={`Rent Now — ${car.name}`}
                      >
                        Rent Now
                      </Link>
                    </Button>
                  </div>
                </div>
              </article>
            </CarouselItem>
          );
        })}
      </CarouselContent>
      <div className="mt-6 flex justify-center gap-3">
        <CarouselPrevious className="static translate-y-0" />
        <CarouselNext className="static translate-y-0" />
      </div>
    </Carousel>
  );
}
